import { Directive, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CallState } from '@lcs/shared/api';
import { select, Store } from '@ngrx/store';
import { OrderingOption } from 'generated/graphql-types';
import { Observable, Subject } from 'rxjs';
import { RegisterActions } from './register-actions';
import { RegisterOptions } from './register-options';
import { RegisterSelectors } from './register-selectors';

@Directive()
export abstract class Register<TItem, TRegisterOptions extends RegisterOptions, TOrderingOptions extends OrderingOption>
   implements OnInit, OnDestroy
{
   callState$: Observable<CallState>;

   dataSource$: Observable<TItem[] | null>;

   isLoading$: Observable<boolean>;

   totalCount$: Observable<number>;

   hasNextPage$: Observable<boolean>;

   pagingCallState$: Observable<CallState>;

   orderingOptions$: Observable<TOrderingOptions[] | null>;

   destroyed$ = new Subject<void>();

   /**
    * When a register is the routed page component (not embedded in a detail tab, dialog, etc.),
    * its host must fill the layout so the register view's `cdkScrollable` table wrapper can
    * overflow and scroll. Without this the page grows to fit every row, so the table never
    * scrolls and infinite-scroll paging never fires.
    * This replaces the list we had to manually upkeep in _globals.scss
    */
   @HostBinding('style.height')
   readonly routedPageHostHeight: string | null =
      inject(ActivatedRoute, { optional: true })?.component === this.constructor ? '100%' : null;

   abstract columns: string[];

   abstract entityName: string;

   constructor(
      public store: Store,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      protected selectors: RegisterSelectors<TItem, any, TRegisterOptions, TOrderingOptions>,
      protected actions: RegisterActions<TItem>
   ) {
      this.callState$ = this.store.pipe(select(this.selectors.selectCallState));
      this.dataSource$ = this.store.pipe(select(this.selectors.selectAll));
      this.isLoading$ = this.store.pipe(select(this.selectors.selectLoading));
      this.totalCount$ = this.store.pipe(select(this.selectors.selectTotalCount));
      this.hasNextPage$ = this.store.pipe(select(this.selectors.selectHasNextPage));
      this.pagingCallState$ = this.store.pipe(select(this.selectors.selectPagingCallState));
      this.orderingOptions$ = this.store.pipe(select(this.selectors.selectOrderingOptions));
   }

   ngOnInit() {
      this.store.dispatch(this.actions.pageEnter());
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onQuickFilterChange(options: TRegisterOptions): void {
      this.store.dispatch(
         this.actions.quickFilterChanged({
            quickFilters: options,
         })
      );
   }

   onOrderingOptionsChange(orderingOptions: TOrderingOptions[]): void {
      this.store.dispatch(
         this.actions.orderingOptionsChanged({
            orderingOptions: orderingOptions,
         })
      );
   }

   onLoadMore(): void {
      this.store.dispatch(this.actions.loadPage());
   }
}
